import { CarouselContentType } from '@unifyapps/defs/blocks/Carousel/types';
import { SwipeableStackContentType } from '@unifyapps/defs/blocks/SwipeableStack/types';
import type { BlockType } from '@unifyapps/defs/types/block';
import type { RepeatableBlockStateType } from '@unifyapps/defs/types/blocks';
import { RUN_TIME_BLOCK_KEY } from '../const';
import { getPrimaryKeyValue } from './getPrimaryKeyValue';

export const RUNTIME_BLOCK_ID_SEPARATOR = '_';

const ORIGINAL_BLOCK_ID_CONVENTION = /^b_[A-Za-z0-9]+$/;

let warnedConventionViolations: Set<string> | undefined;

export function getRuntimeBlockId({
  originalBlockId,
  runtimeBlockKey,
}: {
  originalBlockId: string;
  runtimeBlockKey: string;
}) {
  if (
    process.env.NODE_ENV !== 'production' &&
    !ORIGINAL_BLOCK_ID_CONVENTION.test(originalBlockId)
  ) {
    warnedConventionViolations ??= new Set<string>();
    if (!warnedConventionViolations.has(originalBlockId)) {
      warnedConventionViolations.add(originalBlockId);
      // eslint-disable-next-line no-console -- dev-only invariant warning
      console.warn(
        '[getRuntimeBlockId] originalBlockId violates `b_<alnum>` convention — ' +
          'parseRuntimeBlockId / getOriginalBlockId will silently mis-route this id. ' +
          `Got: "${originalBlockId}"`,
      );
    }
  }

  return `${RUN_TIME_BLOCK_KEY}${RUNTIME_BLOCK_ID_SEPARATOR}${originalBlockId}${RUNTIME_BLOCK_ID_SEPARATOR}${runtimeBlockKey}`;
}

export function getOriginalBlockId(runTimeBlockId: string) {
  if (!isRuntimeBlockId(runTimeBlockId)) {
    return runTimeBlockId;
  }

  // Format: `runTimeBlockKey_<originalBlockId>_<instanceKeyChain>`. Block IDs
  // are auto-generated as `b_<single-segment>` (no `_` inside the segment), so
  // the boundary between original block id and instance key is the SECOND `_`
  // — i.e. the first `_` after the `b_<X>` prefix. Everything past that point
  // is the instance key chain, which CAN contain `_` (entity-id primary keys
  // like `e_<uuid>` and nested chains joined by `_`).
  const prefixEnd = runTimeBlockId.indexOf(RUNTIME_BLOCK_ID_SEPARATOR);
  const blockIdInnerSep = runTimeBlockId.indexOf(RUNTIME_BLOCK_ID_SEPARATOR, prefixEnd + 1);
  if (blockIdInnerSep === -1) return runTimeBlockId;
  const boundary = runTimeBlockId.indexOf(RUNTIME_BLOCK_ID_SEPARATOR, blockIdInnerSep + 1);
  if (boundary === -1) return runTimeBlockId.slice(prefixEnd + 1);
  return runTimeBlockId.slice(prefixEnd + 1, boundary);
}

export const isRuntimeBlockId = (blockId: string) => blockId.startsWith(RUN_TIME_BLOCK_KEY);

export const getRuntimeChildPrefix = (childBlockId: string): string =>
  `${RUN_TIME_BLOCK_KEY}${RUNTIME_BLOCK_ID_SEPARATOR}${childBlockId}${RUNTIME_BLOCK_ID_SEPARATOR}`;

export const isRuntimeSourceBlock = (block: BlockType) => {
  switch (block.component.componentType) {
    case 'TreeView':
    case 'Repeatable':
      return true;
    case 'Carousel': {
      if (block.component.content.type === CarouselContentType.DYNAMIC) {
        return true;
      }
      return false;
    }
    case 'SwipeableStack': {
      if (block.component.content.type === SwipeableStackContentType.DYNAMIC) {
        return true;
      }
      return false;
    }
    default:
      return false;
  }
};

export const isCollectingChildStatesEnabledForBlock = (block: BlockType) => {
  if (block.component.componentType === 'Repeatable') {
    return block.component.content.collectChildStates;
  }
  if (block.component.componentType === 'TreeView') {
    return block.component.content.collectChildStates;
  }
  if (
    block.component.componentType === 'Carousel' &&
    block.component.content.type === CarouselContentType.DYNAMIC
  ) {
    return block.component.content.collectChildStates;
  }
  if (
    block.component.componentType === 'SwipeableStack' &&
    block.component.content.type === SwipeableStackContentType.DYNAMIC
  ) {
    return block.component.content.collectChildStates;
  }
  return false;
};

export type StateDetails = Record<
  string,
  { name: string; blockStateReference: string; children?: StateDetails }
>[];

export const getAllRuntimeChildrenStateOfRuntimeSourceBlock = ({
  children,
  computedRuntimeSourceBlockDetails,
  previousPrimaryKey,
}: {
  children: BlockType[] | undefined;
  computedRuntimeSourceBlockDetails: {
    data: unknown[] | undefined;
    primaryKey: RepeatableBlockStateType['content']['primaryKey'];
  };
  previousPrimaryKey?: string;
}): StateDetails => {
  if (!children) {
    return [];
  }
  const { data, primaryKey } = computedRuntimeSourceBlockDetails;

  const datumChildren = [] as StateDetails;

  for (const index in data) {
    const datum = data[index];
    if (!datum) continue;
    const allChildren = {};

    const primaryKeyValue = getPrimaryKeyValue({
      dataItem: datum,
      index: Number(index),
      primaryKey,
    });

    for (const childIndex in children) {
      const child = children[childIndex];
      const runtimeBlockKey = getRuntimeBlockId({
        originalBlockId: child.id,
        runtimeBlockKey: previousPrimaryKey
          ? `${previousPrimaryKey}${RUNTIME_BLOCK_ID_SEPARATOR}${primaryKeyValue}`
          : primaryKeyValue,
      });

      allChildren[runtimeBlockKey] = {
        name: child.displayName,
        blockStateReference: `{{ runtimeBlocks['${runtimeBlockKey}'] }}`,
      };
    }
    datumChildren.push(allChildren);
  }

  return datumChildren;
};

// runtime block id use `_` to separate different parts of the id,
// so we need to sanitize any id that comes from user to avoid issues.
export function getSanitizedRuntimeBlockId(id?: string) {
  return id?.replaceAll(/[^a-zA-Z0-9]/g, '');
}
